import { useEffect, useMemo } from 'react';
import './Home.scss';
import dog_cat from '/images/dog_cat.png';
import dog from '/images/dog.png';
import packages_image from '/images/packages-image.png';
import packages_image_dark from '/images/packages-image-dark.png';
import upload from '/icons/upload.svg';
import Heading from '../components/ui/Heading';
import pet_icon1 from '/icons/why-us-icon-1.svg';
import pet_icon2 from '/icons/why-us-icon-2.svg';
import pet_icon3 from '/icons/why-us-icon-3.svg';
import how_it_works1 from '/images/how-it-works-1.svg';
import how_it_works2 from '/images/how-it-works-2.svg';
import how_it_works3 from '/images/how-it-works-3.svg';
import dog_upload_field from '/images/dog_upload-field.png';
import Button from '../components/ui/Button';
import "swiper/css/pagination";
import { useDispatch, useSelector } from 'react-redux';
import useFileHandlers from '../hooks/useFileHandlers';
import { resetCustomizer, setCustomizerStep } from '../store/customizerSlice';
import { TK_FLAGS } from '../config/endpoints';
import { resetIndexedDB } from '../utils/indexedDB';
import { resetOrderDetails } from '../store/checkoutSlice';
import { useAffiliate } from '../hooks/useAffiliate';
import { Oval } from 'react-loader-spinner';
import { useIsPartnerAffiliate } from '../hooks/useIsPartnerAffiliate';
import AffiliateProducts from '../components/product/AffiliateProducts';
import CtaProducts from '../components/product/CtaProducts';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { trackCTA } from '../utils/trackCTA';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import HomeProducts from '../components/product/HomeProducts';


function Home({ isMobile }) {
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const { handleFileSelect, handleDrop } = useFileHandlers();

  // Product-first flow (L9): the shopper picks a product FIRST, then personalizes in
  // the designer modal — the homepage photo-dropzone and step copy adapt accordingly.
  const designerFlow = TK_FLAGS.designer;
  const goToProducts = (e) => {
    e?.preventDefault?.();
    navigate('/product-select');
    trackCTA('Start Personalizing');
  };
  const howItWorksSteps = designerFlow
    ? [
      { icon: how_it_works3, title: "Choose a Product", text: "Pick from vet-recommended treats, supplements, and more." },
      { icon: how_it_works1, title: "Add Your Pet's Photo", text: "Upload a cute pic right on the product." },
      { icon: how_it_works2, title: "Personalize & Order", text: "Pick a fun frame, add their name — shipped right to your door!" },
    ]
    : [
      { icon: how_it_works1, title: "Upload Pet Photo", text: "Take a cute pic of your pet!" },
      { icon: how_it_works2, title: "Pick a Label", text: "Choose a fun design and add their photo." },
      { icon: how_it_works3, title: "Shop & Get Your Treats", text: "Your pet's pic on a bag, shipped right to your door!" },
    ];
  const stepBoxes = howItWorksSteps.map((step) => (
    <div key={step.title} className="rounded-20 p-4 box-shadow py-8 gap-8 flex flex-col items-center">
      <div className="flex flex-col justiy-center items-center gap-4 md:gap-8">
        <img src={step.icon} alt="" />
        <h4 className="form-title body-heading">{step.title}</h4>
        <p className="text-center">{step.text}</p>
      </div>
    </div>
  ));
  const cartItems = useSelector((state) => state.cart.cartItems);
  const { affiliate, isLoading } = useAffiliate();
  const colorMode = useSelector((state) => state.ui.colorMode);
  const isPartnerAffiliate = useIsPartnerAffiliate();
  const affiliateAbout = useMemo(
    () => sanitizeHtml(affiliate?.about ?? ""),
    [affiliate?.about]
  );

  //Reset customizer states
  useEffect(() => {
    localStorage.removeItem("petType");
    localStorage.removeItem("petName");
    localStorage.removeItem("imageMetadata");
    localStorage.removeItem("label_template");
    resetIndexedDB("uploadedImages", cartItems);
    resetIndexedDB("resultImages", cartItems);
    resetIndexedDB("onProductImages", cartItems);
    dispatch(setCustomizerStep(1));
    dispatch(resetOrderDetails());
    dispatch(resetCustomizer());
  }, []);

  useEffect(() => {
    if (affiliate && isPartnerAffiliate && !affiliate?.enable_shop) {
      navigate(`/?ref=${affiliate.code}`, {
        replace: true,
        preserveAffiliatePath: false,
      });
    } else if (!affiliate && isPartnerAffiliate && !isLoading) {
      navigate(`/`, {
        replace: true,
        preserveAffiliatePath: false,
      });
    }
  }, [affiliate, isPartnerAffiliate, navigate]);


  if (isLoading) {
    return (
      <div className="flex justify-center items-center w-full h-screen">
        <Oval
          visible={true}
          height={50}
          width={50}
          color="#a99cdf"
          strokeWidth="3"
          strokeWidthSecondary="3"
          secondaryColor="#e2e6ff"
          ariaLabel="oval-loading"
        />
      </div>
    );
  }

  if (isPartnerAffiliate) {
    return (
      <div className='affiliatesHome'>

        <div className='home-hero full-width'>
          <div className='home-hero-inner contained-width items-center justify-between'>
            <div className='flex flex-col gap-2 text-center md:text-left items-center md:items-start md:gap-10 mt-0 md:mt-0'>
              {affiliate && (
                <div className='affiliate-logo-cont flex w-full justify-start md:mt-0'>
                  <img
                    src={affiliate.logo}
                    alt={`${affiliate.name} logo`}
                    className='affiliate-logo'
                  />
                </div>
              )}
              <h1 style={{ whiteSpace: 'nowrap' }}>make<br />your pet<br /><span>a star</span></h1>
              <Button type="button" variant="filled" className='mt-5' style={{ maxWidth: 'fit-content' }} onClick={() => {
                navigate(designerFlow ? '/product-select' : '/customizer');
                trackCTA('Start Personalizing');
              }}>
                Start Personalizing
              </Button>
            </div>
            <div className='flex items-center justify-center max-w-[600px]'>
              <img src={affiliate?.featured_image_url || dog_cat} alt="" className="hero-image w-full max-w-none p-5 mdp-10 ml-0 md:ml-[100px] max-h-[700px] object-contain" />
            </div>
          </div>
        </div>



        <section className='slider-products full-width flex justify-center'>
          <div className='contained-width'>
            <div className='flex flex-col gap-5 md:gap-10 items-center mb-0 md:mb-10'>
              {affiliate && (
                <img
                  src={affiliate.logo}
                  alt={`${affiliate.name} logo`}
                  className='affiliate-logo'
                />
              )}
              {affiliateAbout && (
                <div
                  className="affiliate-about text-center max-w-[700px] mx-auto color-swap"
                  dangerouslySetInnerHTML={{ __html: affiliateAbout }}
                />
              )}
              <h3 className='text-center color-swap'>We Recommend the Following Products</h3>
            </div>
            <AffiliateProducts />
          </div>
        </section>

        <section className="personalized-treats full-width flex flex-col">
          <div className="flex-container contained-width">
            <div>
              <img src={colorMode === 'light' ? packages_image : packages_image_dark} alt="" className="w-full" />
            </div>
            <div className="flex flex-col gap-5 md:gap-5 mt-5 md:mt-0">
              <h3 className='color-swap'>Personalized Treats<br />For Your Pet</h3>
              <div>
                <p className='color-swap'>
                  Your pet is one of a kind - why shouldn’t their treats be too? With Treatink, you can easily personalize pet food, treats, and more by adding your pet’s photo.
                </p>
                <p className="mb-0 color-swap">
                  {designerFlow
                    ? "Just click the button, pick a product, and add a picture of your pet — your custom design is minutes away!"
                    : "Just click the button or drag a photo into the area below to upload a picture of  your pet — and you can start creating your art right away!"}
                </p>
              </div>
              <Button type="button" variant="filled" className='mt-5' onClick={() => {
                navigate(designerFlow ? '/product-select' : '/customizer');
                trackCTA('Start Personalizing');
              }}
                style={{ maxWidth: 'fit-content' }}
              >
                Start Personalizing
              </Button>
            </div>
          </div>
        </section>

        <section className="why-us-container full-width flex justify-center" id="how-it-works">
          <div className="contained-width flex flex-col gap-10">
            <Heading
              h4Text="It’s easy!"
              h2Text="How It Works"
              h4Size="small"
              h2Size="small"
              align="center"
              h4Color="secondary"
            />
            <div className="why-us-boxes grid grid-cols-1 sm:grid-cols-3 md:grid-cols-3 gap-5">
              {stepBoxes}
            </div>
          </div>
        </section>

        {/* <section className='clinic-upload full-width flex justify-center'>
          <div className='contained-width flex justify-center rounded-20 overflow-hidden'>
            <img src={dog_upload_field} alt="" />
            <div
              className="upload-area w-full border-3 border-dashed rounded-20 flex items-center justify-center cursor-pointer transition"
              onClick={designerFlow ? goToProducts : () => document.getElementById('fileInput').click()}
              onDrop={designerFlow ? goToProducts : handleDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              <div className="flex flex-col items-center">
                <img src={upload} alt="" />
                {designerFlow ?
                  <p>Pick a product and add<br />
                    your pet’s photo!</p>
                  : isMobile ?
                    <p>Select your pet’s photo<br />
                      and start personalizing!</p>
                    :
                    <p>Drag your pet’s photo here<br />
                      and start personalizing!</p>
                }
              </div>
              <input
                id="fileInput"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
          </div>
        </section> */}
      </div >
    );
  }

  return (
    <>
      <div className='home-hero full-width'>
        <div className='flex-container contained-width'>
          <div className='flex flex-col gap-5 md:gap-10 mt-5 md:mt-0'>
            <h1>Make Your Pet<br /> a Star!</h1>
            <p className='text-mid mb-0'>
              Treatink lets you easily add your pet’s photo to veterinary recommended treats, supplements, and more - because every tail deserves a personal touch.
            </p>
            {designerFlow && (
              <Button type="button" variant="filled" style={{ maxWidth: 'fit-content' }} onClick={goToProducts}>
                Select Product
              </Button>
            )}
            {affiliate && (
              <img
                src={affiliate.logo}
                alt={`${affiliate.name} logo`}
                className='affiliate-logo'
              />
            )}
          </div>
          <div>
            <img src={dog} alt="" className="w-full" />
          </div>
        </div>


      </div>

      {/* <HomeProducts isMobile={isMobile} /> */}

      <section className="why-us-container full-width flex justify-center" id="how-it-works">
        <div className="contained-width flex flex-col gap-10">
          <Heading
            h4Text="It’s easy!"
            h2Text="How It Works"
            h4Size="small"
            h2Size="small"
            align="center"
            h4Color="secondary"
          />
          <div className="why-us-boxes grid grid-cols-1 sm:grid-cols-3 md:grid-cols-3 gap-5">
            {stepBoxes}
          </div>
          <div className='flex justify-center'>
            <Button
              type="button"
              variant="filled"
              style={{ maxWidth: 'fit-content' }}
              onClick={() => {
                navigate(designerFlow ? '/product-select' : '/customizer');
                trackCTA('Try Now');
              }}
            >
              Try Now
            </Button>
          </div>
        </div>
      </section>

      <section className="slider-products home-slider-section full-width flex justify-center">
        <div className="contained-width flex flex-col gap-5">
          <Heading
            h2Text="Personalized Treats"
            h2Size="big"
            align="center"
          />
          <CtaProducts />
        </div>

        {/* <div className="flex-container contained-width">
          <div className="flex flex-col gap-5 md:gap-10 mt-5 md:mt-0">
            <Heading
              h4Text="Pet Photo Customizer"
              h2Text="Your Pet, Everywhere!"
              h4Size="small"
              h2Size="big"
              align="left"
              h4Color="secondary"
            />
            <div>
              <p>
                Your pet is one of a kind - why shouldn’t their treats be too? With Treatink, you can easily personalize pet veterinary recommended treats, supplements, and more by adding your pet’s photo.
              </p>
              <p className="mb-0">
                Whether it’s a fun gift or a special surprise for your furry friend, we make personalization easy and adorable. Because every tail deserves a unique touch!
              </p>
            </div>
            <Button type="link" variant="filled" link={designerFlow ? "/product-select" : "/customizer"}>
              Start Personalizing
            </Button>
          </div>
          <div>
            <img src={dog2} alt="" className="w-full" />
          </div>
        </div> */}


        {/* <div className="home-slider-container contained-width">
          <Swiper
            modules={[Pagination]}
            pagination={{ el: ".slider-pagination", clickable: true }}
            spaceBetween={10}
            slidesOffsetAfter={0}
            slidesPerGroup={1}
            breakpoints={{
              300: { slidesPerView: 1 },
              700: { slidesPerView: 2 },
              1000: { slidesPerView: 3 },
            }}
          >
            {sliderImages.map((image, index) => (
              <SwiperSlide key={index}>
                <img src={image} alt="" className="w-full h-full object-cover" />
              </SwiperSlide>
            ))}
          </Swiper>
          <div className="slider-pagination" />
        </div> */}
      </section>

      <section className="why-us-container full-width flex justify-center" id="benefits">
        <div className="contained-width flex flex-col gap-10">
          <Heading
            h4Text="Our Benefits"
            h2Text="Why Pet Parents Love Treatink"
            h4Size="small"
            h2Size="small"
            align="center"
            h4Color="secondary"
          />
          <div className="why-us-boxes grid grid-cols-1 sm:grid-cols-3 md:grid-cols-3 gap-5">
            <div className="rounded-20 p-4 box-shadow py-8 gap-8 flex flex-col items-center">
              <div className="flex flex-col justiy-center items-center gap-4 md:gap-8">
                <img src={pet_icon1} alt="" />
                <h4 className="form-title body-heading">Personalized Treats</h4>
                <p className="text-center">Add your pet’s photo to their favorite veterinary recommended treats, supplements, and more.</p>
              </div>
              <Button type="button" variant="outlined" onClick={() => {
                navigate(designerFlow ? '/product-select' : '/customizer');
                trackCTA('Try Now');
              }}
                style={{ maxWidth: 'fit-content' }}
              >
                Try Now
              </Button>
            </div>
            <div className="rounded-20 p-4 box-shadow py-8 gap-8 flex flex-col items-center">
              <div className="flex flex-col justiy-center items-center gap-4 md:gap-8">
                <img src={pet_icon2} alt="" />
                <h4 className="form-title body-heading">Unique & Fun Gifts</h4>
                <p className="text-center">Perfect for pet lovers and special moments.</p>
              </div>
              <Button type="button" variant="outlined" onClick={() => {
                navigate(designerFlow ? '/product-select' : '/customizer');
                trackCTA('Try Now');
              }}
                style={{ maxWidth: 'fit-content' }}
              >
                Try Now
              </Button>
            </div>
            <div className="rounded-20 p-4 box-shadow py-8 gap-8 flex flex-col items-center">
              <div className="flex flex-col justiy-center items-center gap-4 md:gap-8">
                <img src={pet_icon3} alt="" />
                <h4 className="form-title body-heading">Easy Customization</h4>
                <p className="text-center">Create in just a few clicks - no design skills needed!</p>
              </div>
              <Button type="button" variant="outlined" onClick={() => {
                navigate(designerFlow ? '/product-select' : '/customizer');
                trackCTA('Try Now');
              }}
                style={{ maxWidth: 'fit-content' }}
              >
                Try Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* <section className="clinics-section full-width py-10 flex justify-center">
        <div className='contained-width'>
          <div className="clinics-section-inner flex align-center justify-center items-center rounded-20 p-5 md:p-20">
            <div className="gap-8 flex flex-col align-center justify-center items-center">
              <Heading
                h4Text="For Veterinary Clinics"
                h2Text="Launch your product line!"
                h4Size="big"
                h2Size="small"
                align="center"
                h4Color="primary"
              />

              <p className="text-center">
                Give your clients something special - personalized pet products featuring their furry friends! With Treatink, your veterinary clinic can offer your custom-branded treats, supplements and more. Stand out, build loyalty, and create unique experiences for pet owners.
              </p>

              <div className="w-full rounded-20 p-4 box-shadow py-8 gap-8 flex flex-col items-center bg-white">
                <div className="flex flex-col justiy-center items-center gap-4 md:gap-8">
                  <img src={pet_icon2} alt="" />
                  <h4 className="form-title body-heading">Launch & Delight</h4>
                  <p className="text-center mb-0">Offer custom pet products & delight your clients!</p>
                </div>
                <Button
                  type="button"
                  variant="filled"
                  onClick={() => window.open('https://form.typeform.com/to/TOx0hWX7', '_new', 'noopener,noreferrer')}
                >
                  Contact us
                </Button>
              </div>

            </div>
          </div>
        </div>
      </section> */}
    </>
  );
}

export default Home;
